/* import __COLOCATED_TEMPLATE__ from './predicates-editor.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type AiAgentGuidelineService from 'embercom/services/ai-agent-guideline-service';
import { action } from '@ember/object';
//@ts-ignore no type declaration available for ember-copy
import { copy } from 'ember-copy';
//@ts-ignore no type declaration available for predicates
import PredicateGroup from 'predicates/models/predicate-group';

const ROLE_VALUE = {
  user: 'user_role',
  lead: 'contact_role',
  visitor: 'visitor_role',
};

interface Signature {
  Element: HTMLDivElement;
  Args: {
    predicateGroup?: PredicateGroup;
  };
}

export default class AiAgentEscalationPredicatesEditor extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare aiAgentGuidelineService: AiAgentGuidelineService;
  @service declare notificationsService: $TSFixMe;
  @service declare attributeService: $TSFixMe;
  @service declare appService: AppService;
  @service declare store: $TSFixMe;

  allUserRoles = ['user', 'lead', 'visitor'];

  get currentRolePredicate() {
    return this.predicateGroup?.predicates?.find((p: $TSFixMe) => p.type === 'role');
  }

  get selectedUserRoles() {
    let rolePredicate = this.currentRolePredicate;
    if (rolePredicate && rolePredicate.value) {
      let userRoles = rolePredicate.value
        .map((roleValue: string) => {
          let entry = Object.entries(ROLE_VALUE).find(([, value]) => value === roleValue);
          return entry ? entry[0] : null;
        })
        .filter(Boolean);

      return userRoles.length > 0 ? userRoles : ['user', 'lead', 'visitor'];
    }

    // Default to all user types if no role predicate exists
    return ['user', 'lead', 'visitor'];
  }

  get canUseEscalationRuleAudience() {
    return this.appService.app.canUseFeature('audience-for-escalation-rule');
  }

  get shouldShowRoleSelector() {
    if (!this.canUseEscalationRuleAudience) {
      return true;
    }

    // Only show role selector if a subset of roles is selected
    return this.selectedUserRoles.length < this.allUserRoles.length;
  }

  get logicalSwitcherItems() {
    return [
      {
        text: this.intl.t('users-and-companies.rules-editor.and'),
        value: 'and',
      },
      {
        text: this.intl.t('users-and-companies.rules-editor.or'),
        value: 'or',
      },
    ];
  }

  get predicateGroup() {
    return this.args.predicateGroup || this.aiAgentGuidelineService.escalationPredicateGroup;
  }

  get hasUnsavedChanges() {
    return this.aiAgentGuidelineService.hasUnsavedEscalationChanges;
  }

  get isSaveDisabled() {
    return !this.hasUnsavedChanges;
  }

  _filterOutAttributes = (attributes: any[]) => {
    return attributes.filter((attribute: any) => {
      if (!attribute) {
        return false;
      }
      let isFileType = String(attribute.type ?? '') === 'files';
      let rawIdentifier = attribute.identifier ?? attribute.id ?? '';
      let identifier = String(rawIdentifier).toLowerCase();
      let isAiTopic =
        identifier.includes('ai_insights_topic') ||
        identifier.includes('ai_insights_subtopic') ||
        identifier.includes('ai_insights_topic_subtopic');

      return !isFileType && !isAiTopic;
    });
  };

  get attributeGroupList() {
    // Include user, company, and conversation attributes for escalation rules
    let conversationAttributes = [
      ...this.attributeService.assignmentRulesMessageAttributesAsAttributes,
      ...this.attributeService.assigneeAttributes,
      ...this.attributeService.outboundSupportConversationAttributes,
      ...this.attributeService.nonArchivedConversationCustomAttributes,
    ];

    let allAttributes = [
      ...this.attributeService.conversationChannelAttributes,
      ...this._filterOutAttributes(conversationAttributes),
      ...this.attributeService.filterableUserAttributesForMessaging,
    ];

    if (this.appService.app.companiesActive) {
      allAttributes = allAttributes.concat(
        this.attributeService.filterableCompanyAttributesForMessaging,
      );
    }

    let attributeGroups = this.attributeService.attributesToGroupList(allAttributes);

    // Add ticket attributes if available
    if (this.attributeService.ticketAttributesByType?.length > 0) {
      attributeGroups = attributeGroups.concat(
        this.attributeService.ticketAttributesByType.map((group: any) => ({
          ...group,
          attributes: this._filterOutAttributes(group.attributes || []),
        })),
      );
    }

    return attributeGroups;
  }

  @action
  onPredicatesUpdate(predicates: any[]) {
    // Update the predicate group directly like workflows and conditional branches do
    if (this.predicateGroup) {
      this.predicateGroup.set('predicates', copy(predicates));

      // Mark predicates as changed
      this.aiAgentGuidelineService.markEscalationPredicatesAsChanged();
      this.aiAgentGuidelineService.sendPreviewSettingsUpdatedMessage();
    }
  }

  _createRolePredicate(userRoles: string[]) {
    let roleValues = userRoles.map((role) => {
      if (!(role in ROLE_VALUE)) {
        throw new Error(`Unknown role ${role}`);
      }
      return ROLE_VALUE[role as keyof typeof ROLE_VALUE];
    });

    return {
      attribute: 'role',
      type: 'role',
      comparison: 'in',
      value: roleValues,
    };
  }

  @action
  onUserRoleSelect(userRoles: string[]) {
    if (this.predicateGroup) {
      let currentPredicates = this.predicateGroup.predicates.filter((p: any) => p.type !== 'role');

      let rolePredicate = this._createRolePredicate(userRoles);

      let rolePredicateFragment = PredicateGroup.convertRawPredicates(this.store, [rolePredicate]);
      let updatedPredicates = [...rolePredicateFragment, ...currentPredicates];

      this.predicateGroup.set('predicates', copy(updatedPredicates));

      this.aiAgentGuidelineService.markEscalationPredicatesAsChanged();
      this.aiAgentGuidelineService.sendPreviewSettingsUpdatedMessage();
    }
  }

  @action
  async saveEscalationRules() {
    try {
      // Clean up predicates before saving - remove role predicate if it's the only one
      this.cleanupPredicatesBeforeSave();

      // This will actually save the escalation predicates to the backend
      await this.aiAgentGuidelineService.saveEscalationPredicates();

      // Show success notification
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.guidance.save-success-confirmation'),
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(e);
    }
  }

  cleanupPredicatesBeforeSave() {
    if (!this.predicateGroup) {
      return;
    }

    // Get all non-role predicates
    let nonRolePredicates = this.predicateGroup.predicates.filter((p: any) => p.type !== 'role');

    // If there are no non-role predicates, clear all predicates (including role)
    if (nonRolePredicates.length === 0) {
      this.predicateGroup.set('predicates', copy([]));
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Escalation::PredicatesEditor': typeof AiAgentEscalationPredicatesEditor;
  }
}
